import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CreateRoomCommand,
  IntegratedIReckonRoomsService,
  UpdateIReckonRoomDto,
} from '@rocksoft/integrations-ireckon-sdk';
import { mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  IReckonIntegrationContainerActions,
  IReckonIntegrationResolverActions,
  IntegrationApiActions,
} from './integrated-ireckon-room.actions';

@Injectable()
export class IntegratedIReckonRoomEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(IntegratedIReckonRoomsService);

  integrationsLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IReckonIntegrationResolverActions.iReckonIntegrationRequested),
      mergeMap(({ roomUniqueId }) =>
        safeMap(
          this.api.getIntegratedIReckonRooms({
            hotelCode: undefined,
            unitCode: undefined,
            locationUniqueId: undefined,
            roomUniqueId,
          }),
          (rooms) => IntegrationApiActions.iReckonIntegrationsLoaded({ rooms }),
          (error) => IntegrationApiActions.iReckonIntegrationsLoadedError({ error })
        )
      )
    );
  });

  integrationUpdateEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IReckonIntegrationContainerActions.iReckonIntegrationUpdated),
      mergeMap(({ room }) =>
        room.id // existing room
          ? safeMap(
              this.api.updateIntegratedIReckonRoom({
                integratedRoomId: room.id,
                updateIReckonRoomDto: {
                  integratedIReckonHotelId: room.integratedIReckonHotelId,
                  unitCode: room.unitCode,
                  roomUniqueId: room.roomUniqueId,
                  overriddenHotelCode: room.overriddenHotelCode,
                } satisfies UpdateIReckonRoomDto,
              }),
              () => IntegrationApiActions.iReckonIntegrationUpdated({ room }),
              (error) => IntegrationApiActions.iReckonIntegrationUpdatedError({ room, error })
            )
          : safeMap(
              this.api.addIntegratedIReckonRoom({
                createRoomCommand: {
                  integratedIReckonHotelId: room.integratedIReckonHotelId,
                  unitCode: room.unitCode,
                  roomUniqueId: room.roomUniqueId,
                  overriddenHotelCode: room.overriddenHotelCode,
                } satisfies CreateRoomCommand,
              }),
              (created) => IntegrationApiActions.iReckonIntegrationAdded({ room: created }),
              (error) => IntegrationApiActions.iReckonIntegrationAddedError({ room, error })
            )
      )
    );
  });

  integrationDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IReckonIntegrationContainerActions.iReckonIntegrationDeleted),
      mergeMap(({ room }) =>
        safeMap(
          this.api.deleteIntegratedIReckonRoom({ integratedRoomId: room.id }),
          () => IntegrationApiActions.iReckonIntegrationDeleted({ room }),
          (error) => IntegrationApiActions.iReckonIntegrationDeletedError({ room, error })
        )
      )
    );
  });
}
